import React, { useCallback, useEffect, useState } from 'react';
import { Input, Modal, Select, message } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { fetchDueFollowUps, submitFollowUpAttempt } from '../../services/patientFollowUpService';
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
} from '../../components/ui';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

function resolvePatient(record) {
    const patient = record.patient ?? record.patient_id;
    if (!patient) return { id: null, name: '—', phone: '—' };
    if (typeof patient === 'object') {
        return {
            id: patient.id,
            name: patient.name ?? '—',
            phone: patient.phone ?? '—',
        };
    }
    return { id: patient, name: '—', phone: '—' };
}

function resolveDueDate(record) {
    const due = record.due_at ?? record.contact_at ?? record.scheduled_at;
    return due ? dayjs(due).format('DD.MM.YYYY') : '—';
}

export default function PatientFollowUpsDue() {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const [attemptModalOpen, setAttemptModalOpen] = useState(false);
    const [selectedFollowUp, setSelectedFollowUp] = useState(null);
    const [attemptStatus, setAttemptStatus] = useState('no_answer');
    const [attemptDescription, setAttemptDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const loadData = useCallback(async (page = 1, pageSize = 15) => {
        setLoading(true);
        try {
            const response = await fetchDueFollowUps(page, pageSize);
            const rows = response.data ?? [];
            const meta = response.meta ?? {};
            setData(rows);
            setPagination({
                current: meta.current_page ?? page,
                pageSize: meta.per_page ?? pageSize,
                total: meta.total ?? rows.length,
            });
        } catch (error) {
            console.error(error);
            message.error(t('followupsPage.loadError'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const openAttemptModal = (record) => {
        setSelectedFollowUp(record);
        setAttemptStatus('no_answer');
        setAttemptDescription('');
        setAttemptModalOpen(true);
    };

    const closeAttemptModal = () => {
        setAttemptModalOpen(false);
        setSelectedFollowUp(null);
    };

    const handleSubmitAttempt = async () => {
        if (!selectedFollowUp?.id) return;
        setSubmitting(true);
        try {
            await submitFollowUpAttempt(selectedFollowUp.id, {
                status: attemptStatus,
                description: attemptDescription,
            });
            message.success(t('followupsPage.saved'));
            closeAttemptModal();
            loadData(pagination.current, pagination.pageSize);
        } catch (error) {
            if (error.response?.status === 409) {
                message.error(t('followupsPage.alreadyClosed'));
                closeAttemptModal();
                loadData(pagination.current, pagination.pageSize);
            } else {
                message.error(t('followupsPage.saveError'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const statusOptions = [
        { value: 'no_answer', label: t('followupsPage.noAnswer') },
        { value: 'contacted', label: t('followupsPage.contacted') },
        { value: 'will_visit', label: t('followupsPage.willVisit') },
    ];

    const columns = [
        {
            title: t('common.patient'),
            key: 'patient',
            render: (_, record) => {
                const p = resolvePatient(record);
                return p.id ? (
                    <Link
                        to={`/patient/${p.id}`}
                        className="block text-sm font-medium text-[#1D2C7B] hover:underline min-w-[150px]"
                    >
                        {p.name}
                    </Link>
                ) : (
                    <span className="text-sm font-medium text-[#1D2C7B]">{p.name}</span>
                );
            },
        },
        {
            title: t('common.phone'),
            key: 'phone',
            width: 180,
            render: (_, record) => {
                const phone = resolvePatient(record).phone;
                return phone && phone !== '—' ? (
                    <a href={`tel:+${phone}`} className="text-sm text-[#1D2C7B] tabular-nums whitespace-nowrap">
                        {phone}
                    </a>
                ) : (
                    <span className="text-sm text-slate-500">{phone}</span>
                );
            },
        },
        {
            title: t('followupsPage.dueDate'),
            key: 'due_at',
            width: 180,
            render: (_, record) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{resolveDueDate(record)}</span>
            ),
        },
        {
            title: t('common.actions'),
            key: 'action',
            width: 176,
            fixed: 'right',
            render: (_, record) => (
                <ActionButton
                    collapseOnMobile={false}
                    className="!h-8 !px-2.5 sm:!px-2.5 !text-xs"
                    onClick={() => openAttemptModal(record)}
                >
                    {t('followupsPage.enterResult')}
                </ActionButton>
            ),
        },
    ];

    const renderMobileCard = (record) => {
        const p = resolvePatient(record);

        return (
            <div>
                <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                        {p.id ? (
                            <Link to={`/patient/${p.id}`} className="block text-sm font-semibold text-[#1D2C7B] truncate">
                                {p.name}
                            </Link>
                        ) : (
                            <span className="block text-sm font-semibold text-[#1D2C7B] truncate">{p.name}</span>
                        )}
                    </div>
                    <RowIconButton onClick={() => openAttemptModal(record)} aria-label={t('followupsPage.enterResult')}>
                        <PencilSquareIcon className="w-[18px] h-[18px]" />
                    </RowIconButton>
                </div>

                <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                    <span className="tabular-nums">{resolveDueDate(record)}</span>
                    {p.phone && p.phone !== '—' && (
                        <a href={`tel:+${p.phone}`} className="tabular-nums text-[#1D2C7B]">
                            {p.phone}
                        </a>
                    )}
                </div>
            </div>
        );
    };

    return (
        <PageShell>
            <PageCard>
                <PageToolbar title={t('followupsPage.title')} />

                <p className="m-0 px-2.5 sm:px-3 pt-2.5 text-xs text-slate-500">
                    {t('followupsPage.dueHint', { count: pagination.total })}
                </p>

                <DataTable
                    rowKey={(row) => row.id}
                    loading={loading}
                    dataSource={data}
                    columns={columns}
                    mobileCard={renderMobileCard}
                    scrollX={900}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                        showTotal: (total) => t('followupsPage.total', { total }),
                    }}
                    onChange={(pager) => loadData(pager.current, pager.pageSize)}
                />
            </PageCard>

            <Modal
                title={t('admissions.callResult')}
                open={attemptModalOpen}
                onCancel={closeAttemptModal}
                onOk={handleSubmitAttempt}
                confirmLoading={submitting}
                okText={t('common.save')}
                cancelText={t('common.cancel')}
                centered
                width="100%"
                style={{ maxWidth: 520 }}
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{t('common.status')}</label>
                        <Select
                            className="w-full"
                            value={attemptStatus}
                            options={statusOptions}
                            onChange={setAttemptStatus}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{t('followupsPage.remarkOptional')}</label>
                        <Input.TextArea
                            rows={4}
                            value={attemptDescription}
                            onChange={(e) => setAttemptDescription(e.target.value)}
                            placeholder={t('followupsPage.remarkPh')}
                        />
                    </div>
                </div>
            </Modal>
        </PageShell>
    );
}
